import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'shared/hooks/useAuth';
import { ROUTES } from 'shared/constants/routes';
import { ONBOARDING_STEPS, SAAS_PLANS } from 'modules/arena/onboarding/content';
import { CancellationWindow, OnboardingCourt, OnboardingTeamMember, TeamRole } from 'modules/arena/onboarding/types';
import { submitOnboarding } from 'modules/arena/onboarding/onboardingService';
import { useArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { useToast } from 'shared/components/ui/Toast';

function createId() {
  return Math.random().toString(36).slice(2, 9);
}

export function useArenaOnboarding() {
  const { user, impersonate } = useAuth();
  const { saveProfile } = useArenaProfile();
  const toast = useToast();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);

  const [arenaName, setArenaName] = useState('');
  const [unitName, setUnitName] = useState('Unidade Principal');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('Fortaleza-CE');

  const [selectedSportIds, setSelectedSportIds] = useState<string[]>(['Beach Tennis']);

  const [courts, setCourts] = useState<OnboardingCourt[]>([
    { id: createId(), name: 'Quadra 1', sportIds: ['Beach Tennis'], pricePerHour: 90 },
  ]);

  const [openDays, setOpenDays] = useState<string[]>(['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']);
  const [openTime, setOpenTime] = useState('07:00');
  const [closeTime, setCloseTime] = useState('22:00');

  const [team, setTeam] = useState<OnboardingTeamMember[]>([]);

  const [cancellationWindow, setCancellationWindow] = useState<CancellationWindow>('24h');
  const [refundPercentage, setRefundPercentage] = useState(100);

  const [planId, setPlanId] = useState(SAAS_PLANS[1].id);

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === ONBOARDING_STEPS.length - 1;

  function next() {
    setStepIndex((current) => Math.min(current + 1, ONBOARDING_STEPS.length - 1));
  }

  function back() {
    setStepIndex((current) => Math.max(current - 1, 0));
  }

  function toggleSport(sport: string) {
    setSelectedSportIds((prev) =>
      prev.includes(sport) ? prev.filter((item) => item !== sport) : [...prev, sport]
    );
  }

  function addCourt() {
    setCourts((prev) => [...prev, { id: createId(), name: `Quadra ${prev.length + 1}`, sportIds: [], pricePerHour: 80 }]);
  }

  function updateCourt(id: string, patch: Partial<OnboardingCourt>) {
    setCourts((prev) => prev.map((court) => (court.id === id ? { ...court, ...patch } : court)));
  }

  /** Substitui a lista de quadras (usado pela simulação automática). */
  function replaceCourts(next: Omit<OnboardingCourt, 'id'>[]) {
    setCourts(next.map((court) => ({ ...court, id: createId() })));
  }

  function removeCourt(id: string) {
    setCourts((prev) => prev.filter((court) => court.id !== id));
  }

  function toggleCourtSport(courtId: string, sport: string) {
    setCourts((prev) =>
      prev.map((court) =>
        court.id === courtId
          ? {
              ...court,
              sportIds: court.sportIds.includes(sport)
                ? court.sportIds.filter((item) => item !== sport)
                : [...court.sportIds, sport],
            }
          : court
      )
    );
  }

  function toggleDay(day: string) {
    setOpenDays((prev) => (prev.includes(day) ? prev.filter((item) => item !== day) : [...prev, day]));
  }

  function addTeamMember(name: string, role: TeamRole) {
    if (!name.trim()) return;
    setTeam((prev) => [...prev, { id: createId(), name, role }]);
  }

  function removeTeamMember(id: string) {
    setTeam((prev) => prev.filter((member) => member.id !== id));
  }

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function finish() {
    setIsSubmitting(true);
    try {
      await submitOnboarding({
        arenaName,
        unitName,
        address,
        city,
        selectedSportIds,
        courts,
        openDays,
        openTime,
        closeTime,
        team,
        cancellationWindow,
        refundPercentage,
        planId,
      });
      // A arena cadastrada vira a arena da demonstração (menu, agenda, quadras, relatórios).
      const name = arenaName.trim() || 'Minha Arena';
      saveProfile({
        arenaName: name,
        unitName: unitName.trim() || 'Unidade Principal',
        address: address.trim(),
        city,
        sports: selectedSportIds,
        courts: courts.filter((court) => court.name.trim()),
        openDays,
        openTime,
        closeTime,
        team,
        cancellationWindow,
        refundPercentage,
        planId,
        ownerName: user?.name,
      });
      // Quem acabou de criar a conta de dono continua sendo ele; só sem conta caímos na persona demo.
      if (user?.role !== 'arena') impersonate('arena');
      navigate(ROUTES.ARENA_DASHBOARD, { replace: true });
      toast({ title: `${name} está no ar!`, description: 'Este é o seu painel. Siga o roteiro da demonstração no canto da tela.' });
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    stepIndex,
    setStepIndex,
    isFirstStep,
    isLastStep,
    next,
    back,
    arenaName,
    setArenaName,
    unitName,
    setUnitName,
    address,
    setAddress,
    city,
    setCity,
    selectedSportIds,
    toggleSport,
    courts,
    addCourt,
    updateCourt,
    replaceCourts,
    removeCourt,
    toggleCourtSport,
    openDays,
    toggleDay,
    openTime,
    setOpenTime,
    closeTime,
    setCloseTime,
    team,
    addTeamMember,
    removeTeamMember,
    cancellationWindow,
    setCancellationWindow,
    refundPercentage,
    setRefundPercentage,
    planId,
    setPlanId,
    finish,
    isSubmitting,
  };
}

export type ArenaOnboardingState = ReturnType<typeof useArenaOnboarding>;
